// FR-AD1 "org admin console: members, teams, SSO, security policy, retention, storage and
// usage dashboard." Retention/security-policy are still out of scope (build-plan-m1.md §6) —
// this shell covers members, teams, audit log, settings (which folds in the usage dashboard,
// since there's no separate route for it in REQUIREMENTS.md), SSO (FR-S1/S4/S5/S6/S9,
// OIDC only — see server/src/sso.rs's module doc for the exact scope cut from §4.3), and
// AI / BYOB transcription (FR-AI7 / FR-BY1 / FR-BY5).
//
// Shares AppShell with the rest of the org app so admin isn't a second, narrower sidebar.
import { Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { Tabs } from "@grackle/ui";
import { AppShell } from "../components/AppShell";
import { PageLoader } from "../components/ui";
import { useSession } from "../session";
import { adminPath, libraryPath } from "../library/routes";
import type { OrgOut } from "../api/types";
import "./admin.css";

export interface AdminOutletContext {
  org: OrgOut;
}

const TABS = [
  { value: "", label: "Overview" },
  { value: "members", label: "Members" },
  { value: "content", label: "Content" },
  { value: "retention", label: "Retention" },
  { value: "legal-holds", label: "Legal holds" },
  { value: "audit-log", label: "Audit log" },
  { value: "sso", label: "SSO" },
  { value: "ai", label: "AI and models" },
  { value: "integrations", label: "Integrations" },
  { value: "webhooks", label: "Webhooks" },
  { value: "settings", label: "Settings" },
];

export default function AdminLayout() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, orgs, loading } = useSession();
  const org = orgs.find((o) => o.slug === slug);

  if (loading) return <PageLoader label="Loading admin…" />;
  if (!user) return <Navigate to="/signin" replace />;
  if (!org) return <Navigate to="/orgs" replace />;
  // FR-AD1's console is admin/owner territory — a plain member gets sent back to the library
  // rather than a dead-end 404 page, since the org itself is real and visible to them.
  if (org.role !== "owner" && org.role !== "admin") return <Navigate to={libraryPath(org.slug)} replace />;

  return (
    <AppShell org={org} user={user} section="admin">
      <Tabs
        className="admin-tabs"
        overflow="menu"
        items={TABS}
        value={pathname.split("/")[4] ?? ""}
        onChange={(page) => navigate(adminPath(org.slug, page))}
      />
      <div className="admin-content">
        <Outlet context={{ org } satisfies AdminOutletContext} />
      </div>
    </AppShell>
  );
}
